<script setup lang="ts">
import { computed, watch, ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import BottomNav from '@/components/common/BottomNav.vue';
import OfflineBanner from '@/components/common/OfflineBanner.vue';
import NewsletterBanner from '@/components/common/NewsletterBanner.vue';
import { useContextualNotams, positionProviderKind } from '@/composables/useContextualNotams';
import { useGeolocationPermission } from '@/composables/useGeolocationPermission';
import { isSimulating } from '@/composables/useFlightSimulator';
import { trackingStartMode } from '@/utils/trackingStartPolicy';
import { ApproximatePositionProvider } from '@/services/position/ApproximatePositionProvider';
import { useContextualStore } from '@/stores/contextualStore';
import { trackingArmed } from '@/composables/useTrackingArm';
import { useSettingsStore } from '@/stores/settingsStore';

const route = useRoute();

const showBottomNav = computed(() => route.name !== 'results');

// #8 Phase 3g D5: composable hoisted to App.vue (lives for the app lifetime).
// Auto-start when route='tracking' AND (permission='granted' OR 'unsupported').
//
// 'granted': obvious — fire watchPosition immediately.
// 'unsupported': pre-Safari-16.4 / older Android WebView have no Permissions API
//   but DO have geolocation; the only way to discover whether it works is to
//   call watchPosition (PR #46 review F7). On true failure the composable's
//   _onError lands at status='permission_denied' and stop()s itself.
// 'prompt' / 'unsupported': inert until a tap arms the loop (#525,
//   useTrackingArm) — the site root opens the tracker, so an automatic start
//   would put the native location dialog before the first paint.
// 'denied': the loop stays stopped, but the VIEW stays reachable (#449)
//   — the simulated leg needs no location access.
const { start, stop, refresh, isRunning, setPositionProvider, useRealPositionSource } = useContextualNotams();
// One instance for the app's lifetime, like the real receiver.
const approximateSource = new ApproximatePositionProvider();
const contextualStore = useContextualStore();
const { permissionState } = useGeolocationPermission();

// #525 — the header toggle is on the tracker too, and the served NOTAM text
// comes from the API in the requested language: refetch now rather than at
// the next move or forced refresh (which never comes while backing off).
const settingsStore = useSettingsStore();
watch(
  () => settingsStore.locale,
  () => {
    if (isRunning.value) refresh();
  }
);

watch(
  () => [route.name, permissionState.value, trackingArmed.value, isSimulating.value] as const,
  ([name, perm, armed, simulating]) => {
    // #56 let the native prompt fire on entering /tracking; #525 reverses
    // that now that '/' lands here: the prompt waits for a tap. The policy
    // itself lives in utils/trackingStartPolicy so it can be tested without
    // mounting the app — and so the simulator's re-arm cannot drift from
    // it. start() is idempotent, so it is a no-op while a simulated leg is
    // already running.
    // #527: four outcomes, decided in trackingStartPolicy. The provider is
    // swapped only when its kind differs — a swap stops the loop and resets
    // the store, so doing it on every re-evaluation would blank the map.
    switch (trackingStartMode(name, perm, simulating, armed)) {
      case 'off':
        stop();
        break;
      case 'keep':
        start();
        break;
      case 'real':
        if (positionProviderKind.value !== 'geolocation') {
          // The swap resets the store. Carry the approximate snapshot over so
          // the map stays where it was until the first real fix lands (PR
          // #528 review).
          const carried = positionProviderKind.value === 'approximate' ? contextualStore.lastPosition : null;
          useRealPositionSource();
          // Carried WITH its provenance: the view keeps the approximate
          // banner and shows no GPS-stale banner until a real fix lands.
          if (carried !== null) contextualStore.carryPosition(carried, 'approximate');
        }
        start();
        break;
      case 'approximate':
        if (positionProviderKind.value !== 'approximate') setPositionProvider(approximateSource);
        start();
        break;
    }
  },
  { immediate: true }
);

// #449 DELETES the PR #46 F3 revocation eject. Revoking location mid-session
// no longer strands the pilot on a dead-end view: the tab stays, the view
// stays, and it shows the permission placeholder with the simulator above
// it. Being moved to /home was the thing that told them nothing — and it
// would now cut a pilot off mid-simulation.

// #56 post-cutover viewport fix: the pilot-facing BottomNav was pushed
// below the visible area on iPhone Safari (URL bar included in `100vh`)
// AND on some older Android WebViews (no support for `100dvh`, which
// shipped Chrome 108 in Dec 2022). The universally-safe pattern is to
// track window.innerHeight in JS and pin it as an inline `height` — that
// value always equals the *visible* viewport on every device we've seen
// this report from. `resize` fires when the URL bar collapses on iOS or
// when the WebView chrome changes on Android. `visualViewport` (where
// available) is the more accurate signal on iOS for the address-bar
// shrink; we listen to both.
const appHeight = ref<string>('100dvh');
function _refreshHeight(): void {
  const vv = (window as unknown as { visualViewport?: { height: number } }).visualViewport;
  const px = vv?.height ?? window.innerHeight;
  if (px > 0) appHeight.value = `${px}px`;
}
onMounted(() => {
  _refreshHeight();
  window.addEventListener('resize', _refreshHeight);
  window.addEventListener('orientationchange', _refreshHeight);
  const vv = (window as unknown as { visualViewport?: EventTarget }).visualViewport;
  vv?.addEventListener('resize', _refreshHeight);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', _refreshHeight);
  window.removeEventListener('orientationchange', _refreshHeight);
  const vv = (window as unknown as { visualViewport?: EventTarget }).visualViewport;
  vv?.removeEventListener('resize', _refreshHeight);
});
</script>

<template>
  <!-- Inline height from window.innerHeight (updated on resize +
       orientationchange + visualViewport.resize). Covers iOS Safari
       URL-bar collapse AND old Android WebViews that lack `dvh` support.
       Initial value is `100dvh` so pre-JS paint doesn't overflow on
       modern browsers; JS overwrites on mount. See <script setup>. -->
  <div class="bg-deck-inset flex flex-col overflow-hidden" :style="{ height: appHeight }">
    <!-- Offline indicator -->
    <OfflineBanner />

    <!-- Main content -->
    <main class="flex-1 overflow-hidden">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- Bottom navigation -->
    <BottomNav v-if="showBottomNav" />

    <!-- Newsletter consent banner -->
    <NewsletterBanner />
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
